import { useMemo } from 'react';
import type { VFC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import {
  isAssetPaywalled,
  isAssetUpcoming,
  isDocumentary,
} from '../../player/playerAssetHelpers';
import {
  isAssetExpired,
  isLiveStream,
} from '../../shared/helpers/assetHelpers';
import { ExpiredDocumentarySplash } from '../../components/ExpiredDocumentarySplash/ExpiredDocumentarySplash';
import { PlayerWithDocumentaryDetails } from '../../components/PlayerWithDocumentaryDetails/PlayerWithDocumentaryDetails';
import { PlayerWithPaywalledDocumentaryDetails } from '../../components/PlayerWithPaywalledDocumentaryDetails/PlayerWithPaywalledDocumentaryDetails';
import { PlayerWithAssetDetails } from '../../components/PlayerWithAssetDetails/PlayerWithAssetDetails';
import { PlayerWithPaywalledAssetDetails } from '../../components/PlayerWithPaywalledAssetDetails/PlayerWithPaywalledAssetDetails';
import { UpcomingAssetSplash } from '../../components/UpcomingAssetSplash/UpcomingAssetSplash';
import { isStartTimeString } from '../../player/playerHelpers';
import { hasAssetAccess } from '../../services/paywall';
import { useQuery } from '../../components/utils/useQuery';
import { useAccessContext } from '../../components/AccessProvider/AccessProvider';
import { usePlayerContext } from '../../player/PlayerContext';
import { usePipContext } from '../../player/PictureInPictureProvider';
import { usePlayerAsset } from '../../player/hooks/usePlayerAsset';
import { useViewportType } from '../../components/utils/useViewportType';

import { AssetSmartSplash } from './AssetSmartSplash';

type Props = {
  asset: SvpAssetAdditional;
};

export const AssetComponent: VFC<Props> = ({ asset }) => {
  const { t: time } = useQuery<'t'>();
  const { accesses } = useAccessContext();
  const currentAsset = usePlayerAsset();
  const { isPipEnabled } = usePipContext();
  const { isCasting } = usePlayerContext();
  const { isMobile } = useViewportType();
  const startTime = useMemo(
    () => (time && isStartTimeString(time) ? parseInt(time) : undefined),
    [time]
  );

  const hasInitialAssetAccess = useMemo(() => {
    if (!asset) {
      return false;
    }
    return hasAssetAccess(asset, accesses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, accesses]);

  if (
    // we want to display controls of the currently played video
    asset.id !== currentAsset?.id &&
    // paywalled splash is currently handled elsewhere (consider moving it here)
    hasInitialAssetAccess &&
    // display splash if pip is enabled or casting
    (isPipEnabled || isCasting)
  ) {
    return <AssetSmartSplash asset={asset} startTime={startTime} />;
  }

  if (isDocumentary(asset)) {
    if (isAssetExpired(asset)) {
      return <ExpiredDocumentarySplash asset={asset} />;
    }

    if (isAssetPaywalled(asset)) {
      if (hasInitialAssetAccess) {
        // omit asset splash for logged-in users with matching access level
        return (
          <PlayerWithDocumentaryDetails asset={asset} startTime={startTime} />
        );
      }

      return (
        <PlayerWithPaywalledDocumentaryDetails
          asset={asset}
          startTime={startTime}
        />
      );
    }

    return <PlayerWithDocumentaryDetails asset={asset} startTime={startTime} />;
  }

  if (isAssetPaywalled(asset)) {
    if (hasInitialAssetAccess && !isAssetUpcoming(asset)) {
      // omit asset splash if the asset can be played instantly for the user
      return <PlayerWithAssetDetails asset={asset} startTime={startTime} />;
    } else {
      return (
        <PlayerWithPaywalledAssetDetails asset={asset} startTime={startTime} />
      );
    }
  }

  if (isAssetUpcoming(asset) && !isLiveStream(asset)) {
    return (
      <UpcomingAssetSplash asset={asset} size={isMobile ? 'medium' : 'large'} />
    );
  }

  return <PlayerWithAssetDetails asset={asset} startTime={startTime} />;
};
